import * as moment from 'moment-timezone';

export enum InviteExpiryMode {
  hours = 'hours',
  dateTime = 'date_time',
}

/** The expiry staff choose when inviting a customer to an event or roster (PRO-1537). */
export interface InviteExpiry {
  mode: InviteExpiryMode;
  hoursToAccept: number | null;
  // `YYYY-MM-DD` / `HH:mm`, in the event's or roster's location zone.
  deadlineDate: string;
  deadlineTime: string;
}

export const INVITE_EXPIRY_INCOMPLETE_ERROR = 'Enter both a date and a time.';
export const INVITE_EXPIRY_PAST_ERROR = 'This date and time has already passed.';
export const INVITE_EXPIRY_HOURS_ERROR = 'Enter a whole number of hours greater than zero.';

function isPositiveIntegerHours(hoursToAccept: number | null): boolean {
  return typeof hoursToAccept === 'number' && Number.isInteger(hoursToAccept) && hoursToAccept > 0;
}

export function blankInviteExpiry(): InviteExpiry {
  return {
    mode: InviteExpiryMode.hours,
    hoursToAccept: null,
    deadlineDate: '',
    deadlineTime: '',
  };
}

/** The past check is skipped while `timeZone` is unknown; the server still rejects a past deadline. */
export function inviteExpiryValidationError(expiry: InviteExpiry, timeZone: string | null): string | null {
  if (expiry.mode === InviteExpiryMode.hours) {
    const hoursToAccept = expiry.hoursToAccept;
    // Null or blank means "no expiry", which is valid; the server treats it the same way.
    if (hoursToAccept === null || hoursToAccept === undefined || (hoursToAccept as unknown) === '') {
      return null;
    }
    return isPositiveIntegerHours(hoursToAccept) ? null : INVITE_EXPIRY_HOURS_ERROR;
  }

  const hasDate = !!expiry.deadlineDate;
  const hasTime = !!expiry.deadlineTime;
  if (hasDate !== hasTime) { return INVITE_EXPIRY_INCOMPLETE_ERROR; }
  if (!hasDate || !timeZone) { return null; }

  const deadline = moment.tz(expiry.deadlineDate + ' ' + expiry.deadlineTime, 'YYYY-MM-DD HH:mm', timeZone);
  if (deadline.isValid() && deadline.isBefore(moment())) { return INVITE_EXPIRY_PAST_ERROR; }
  return null;
}

/** The request values for the active mode only; the server rejects hours and a deadline together. */
export function inviteExpiryRequestFields(expiry: InviteExpiry): {
  hoursToAccept: number | null;
  newDeadlineDate: string;
  newDeadlineTime: string;
} {
  if (expiry.mode === InviteExpiryMode.dateTime) {
    return {
      hoursToAccept: null,
      newDeadlineDate: expiry.deadlineDate || '',
      newDeadlineTime: expiry.deadlineTime || '',
    };
  }
  return {
    hoursToAccept: isPositiveIntegerHours(expiry.hoursToAccept) ? expiry.hoursToAccept : null,
    newDeadlineDate: '',
    newDeadlineTime: '',
  };
}

/** Prefill for an existing deadline, in `timeZone` (the browser's when null). */
export function inviteExpiryFromDeadline(iso8601datetime: string | null, timeZone: string | null): InviteExpiry {
  if (!iso8601datetime) { return blankInviteExpiry(); }

  const deadline = timeZone ? moment.tz(iso8601datetime, timeZone) : moment(iso8601datetime);
  if (!deadline.isValid()) { return blankInviteExpiry(); }

  return {
    mode: InviteExpiryMode.dateTime,
    hoursToAccept: null,
    deadlineDate: deadline.format('YYYY-MM-DD'),
    deadlineTime: deadline.format('HH:mm'),
  };
}

export function inviteExpiriesEqual(a: InviteExpiry, b: InviteExpiry): boolean {
  return a.mode === b.mode &&
    (a.hoursToAccept || null) === (b.hoursToAccept || null) &&
    (a.deadlineDate || '') === (b.deadlineDate || '') &&
    (a.deadlineTime || '') === (b.deadlineTime || '');
}
